// BankDetailsComponent.jsx
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import '../css/BankDetails.css';
import Navbar from './Navbar';
import Footer from './Footer';
import { fetchBankDetails, submitBankDetails } from '../Services/Api';

// Yup validation schema
const bankSchema = yup.object({
  accountHolderName: yup.string().required('Account holder name is required'),
  accountType: yup.string().required('Account type is required'),
  accountNumber: yup.string()
    .required('Account number is required')
    .matches(/^\d{9,18}$/, 'Account number must be 9-18 digits'),
  bankName: yup.string().required('Bank name is required'),
  bankBranch: yup.string().required('Bank branch is required'),
  ifscCode: yup.string()
    .required('IFSC code is required'),
  micrCode: yup.string()
    .required('MICR code is required')
    .matches(/^\d{9}$/, 'MICR code must be 9 digits'),
  bankAddress: yup.string().required('Bank branch address is required'),
  bankDocument: yup.mixed()
    .test('fileRequired', 'A file is required', (value) => {
      // Only validate if we're creating new (not editing existing with document)
      return !(value === undefined || value.length === 0);
    })
    .test('fileSize', 'File too large (max 5MB)', value => {
      return !value || (value && value[0] && value[0].size <= 5000000);
    })
    .test('fileType', 'Unsupported file format', value => {
      return !value || (value && value[0] && ['image/jpeg', 'image/png', 'application/pdf'].includes(value[0].type));
    })
});

const BankDetails = () => {
  const [bankDetails, setBankDetails] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [serverErrors, setServerErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');

  const { register, handleSubmit, formState: { errors }, reset, setError, clearErrors, setValue, watch } = useForm({
    resolver: yupResolver(bankSchema)
  });
 
  const watchBankDocument = watch("bankDocument");

  // Check if bank details exist on component mount
  useEffect(() => {
        document.title = 'Bank Details';

    const fetchFun = async () => {
      try {
        const data = await fetchBankDetails();
        if (data.success) {
          setBankDetails(data.data);
          // Set file preview if document exists
          if (data.data.document_url) {
            setFilePreview(data.data.passbook_image);
          }
        } else {
          console.error("No data found");
        }
      } catch (err) {
        // console.log("Error fetching bank details:", err);
      }
    };
    fetchFun();
  }, []);

  // Update file preview when file input changes
  useEffect(() => {
    if (watchBankDocument && watchBankDocument.length > 0) {
      const file = watchBankDocument[0];
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => setFilePreview(e.target.result);
        reader.readAsDataURL(file);
      } else if (file.type === 'application/pdf') {
        setFilePreview('pdf');
      }
    }
  }, [watchBankDocument]);

  // Function to set server errors to form fields
  const setServerValidationErrors = (errorData) => {
    const newServerErrors = {};

    // Clear previous server errors
    setServerErrors({});

    // Set errors for each field with server validation issues
    Object.keys(errorData).forEach(field => {
      const serverError = errorData[field][0]; // Get first error message
      newServerErrors[field] = serverError;

      // Set error using react-hook-form's setError
      setError(field, {
        type: 'server',
        message: serverError
      });
    });

    // Update server errors state for display
    setServerErrors(newServerErrors);
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setServerErrors({}); // Clear previous server errors
    setSuccessMessage(''); // Clear previous success message

    const formData = new FormData();

    // Append text fields
    formData.append("accountHolderName", data.accountHolderName);
    formData.append("accountType", data.accountType);
    formData.append("accountNumber", data.accountNumber);
    formData.append("bankName", data.bankName);
    formData.append("bankBranch", data.bankBranch);
    formData.append("ifscCode", data.ifscCode);
    formData.append("micrCode", data.micrCode);
    formData.append("bankAddress", data.bankAddress || "");

    // Append file field only if a new file was selected
    if (data.bankDocument && data.bankDocument[0]) {
      formData.append("bankDocument", data.bankDocument[0]);
    }

    try {
      const response = await submitBankDetails(formData);
      // console.log("Success:", response);

      // Update bank details with new data
      const updatedDetails = {
        account_holder_name: data.accountHolderName,
        account_type: data.accountType,
        account_no: data.accountNumber,
        bank_name: data.bankName,
        bank_branch: data.bankBranch,
        ifsc_code: data.ifscCode,
        micr_code: data.micrCode,
        branch_address: data.bankAddress,
        document_url: filePreview
      };
      
      setBankDetails(updatedDetails);

      // Show success message
      setSuccessMessage(response.data.message || 'Bank details saved successfully!');

      // Reset editing state but keep form values
      setIsEditing(false);

      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
    } catch (error) {
      if (error.response && error.response.status === 422) {
        console.error("Validation errors:", error.response.data.errors);
        // Set server validation errors to form fields
        setServerValidationErrors(error.response.data.errors);
      } else {
        console.error("Unexpected error:", error);
        // Show generic error message
        setServerErrors({ general: 'An unexpected error occurred. Please try again.' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      clearErrors('bankDocument');
      setServerErrors(prev => ({ ...prev, bankDocument: undefined }));

      // Create a preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => setFilePreview(e.target.result);
        reader.readAsDataURL(file);
      } else if (file.type === 'application/pdf') {
        setFilePreview('pdf');
      } else {
        setFilePreview(null);
      }
    }
  };

  const handleEdit = () => {
    if (bankDetails) {
      // Pre-populate form with existing data
      setValue('accountHolderName', bankDetails.account_holder_name);
      setValue('accountType', bankDetails.account_type);
      setValue('accountNumber', bankDetails.account_no);
      setValue('bankName', bankDetails.bank_name);
      setValue('bankBranch', bankDetails.bank_branch);
      setValue('ifscCode', bankDetails.ifsc_code);
      setValue('micrCode', bankDetails.micr_code);
      setValue('bankAddress', bankDetails.branch_address);
      
      setIsEditing(true);
    }
  };

  const handleCancel = () => {
    if (bankDetails) {
      setIsEditing(false);
    } else {
      reset();
      setFilePreview(null);
    }
    setServerErrors({});
    setSuccessMessage('');
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete your bank details?')) {
      setBankDetails(null);
      reset();
      setFilePreview(null);
      setServerErrors({});
      setSuccessMessage('Bank details deleted successfully!');

      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
    }
  };

  const downloadDocument = () => {
    if (filePreview && filePreview !== 'pdf') {
      // Create a temporary link for image download
      const link = document.createElement('a');
      link.href = filePreview;
      link.download = 'bank-document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else if (filePreview === 'pdf' && bankDetails && bankDetails.passbook_image) {
      // For PDFs, open in new tab (assuming document_url is a valid URL)
      window.open(`${process.env.File_base_Url}${bankDetails.passbook_image}`, '_blank');
    }
  };

  return (
    <>
      <Navbar />
      <div className="bank-details-container">
        <div className="bank-details-card">
          <div className="bank-details-header">
            <i className="fas fa-university"></i>
            <h1>Programme Implementation Partner Bank Details</h1>
            {/* {bankDetails && !isEditing && (
              <button className="bank-edit-btn" onClick={handleEdit}>
                <i className="fas fa-edit"></i> Edit Details
              </button>
            )} */}
          </div>

          {/* Success message */}
          {successMessage && (
            <div className="success-message">
              <i className="fas fa-check-circle"></i>
              {successMessage}
            </div>
          )}

          {/* General error message */}
          {serverErrors.general && (
            <div className="server-error-general">
              <i className="fas fa-exclamation-circle"></i>
              {serverErrors.general}
            </div>
          )}

          {(!bankDetails || isEditing) ? (
            // FORM VIEW (for new entries or editing)
            <form className="bank-form" onSubmit={handleSubmit(onSubmit)}>
              <div className="bank-form-grid">
                <div className="bank-form-group">
                  <label>Account Holder Name <span className="required">*</span></label>
                  <div className="input-container">
                    <i className="fas fa-user"></i>
                    <input
                      type="text"
                      className={errors.accountHolderName || serverErrors.accountHolderName ? 'error' : ''}
                      {...register('accountHolderName')}
                      placeholder="Enter full name"
                    />
                  </div>
                  {errors.accountHolderName && (
                    <span className="error-message">{errors.accountHolderName.message}</span>
                  )}
                  {serverErrors.accountHolderName && !errors.accountHolderName && (
                    <span className="error-message server-error">{serverErrors.accountHolderName}</span>
                  )}
                </div>

                <div className="bank-form-group">
                  <label>Bank Account Type <span className="required">*</span></label>
                  <div className="input-container">
                    <i className="fas fa-wallet"></i>
                    <select
                      className={errors.accountType || serverErrors.accountType ? 'error' : ''}
                      {...register('accountType')}
                    >
                      <option value="">Select account type</option>
                      <option value="Savings">Savings</option>
                      <option value="Current">Current</option>
                    </select>
                  </div>
                  {errors.accountType && (
                    <span className="error-message">{errors.accountType.message}</span>
                  )}
                  {serverErrors.accountType && !errors.accountType && (
                    <span className="error-message server-error">{serverErrors.accountType}</span>
                  )}
                </div>

                <div className="bank-form-group">
                  <label>Account Number <span className="required">*</span></label>
                  <div className="input-container">
                    <i className="fas fa-hashtag"></i>
                    <input
                      type="text"
                      className={errors.accountNumber || serverErrors.accountNumber ? 'error' : ''}
                      {...register('accountNumber')}
                      placeholder="Enter account number"
                    />
                  </div>
                  {errors.accountNumber && (
                    <span className="error-message">{errors.accountNumber.message}</span>
                  )}
                  {serverErrors.accountNumber && !errors.accountNumber && (
                    <span className="error-message server-error">{serverErrors.accountNumber}</span>
                  )}
                </div>

                <div className="bank-form-group">
                  <label>Bank Name <span className="required">*</span></label>
                  <div className="input-container">
                    <i className="fas fa-building"></i>
                    <input
                      type="text"
                      className={errors.bankName || serverErrors.bankName ? 'error' : ''}
                      {...register('bankName')}
                      placeholder="Enter bank name"
                    />
                  </div>
                  {errors.bankName && (
                    <span className="error-message">{errors.bankName.message}</span>
                  )}
                  {serverErrors.bankName && !errors.bankName && (
                    <span className="error-message server-error">{serverErrors.bankName}</span>
                  )}
                </div>

                <div className="bank-form-group">
                  <label>Bank Branch <span className="required">*</span></label>
                  <div className="input-container">
                    <i className="fas fa-code-branch"></i>
                    <input
                      type="text"
                      className={errors.bankBranch || serverErrors.bankBranch ? 'error' : ''}
                      {...register('bankBranch')}
                      placeholder="Enter branch name"
                    />
                  </div>
                  {errors.bankBranch && (
                    <span className="error-message">{errors.bankBranch.message}</span>
                  )}
                  {serverErrors.bankBranch && !errors.bankBranch && (
                    <span className="error-message server-error">{serverErrors.bankBranch}</span>
                  )}
                </div>

                <div className="bank-form-group">
                  <label>IFSC Code <span className="required">*</span></label>
                  <div className="input-container">
                    <i className="fas fa-qrcode"></i>
                    <input
                      type="text"
                      className={errors.ifscCode || serverErrors.ifscCode ? 'error' : ''}
                      {...register('ifscCode')}
                      placeholder="Enter IFSC code"
                    />
                  </div>
                  {errors.ifscCode && (
                    <span className="error-message">{errors.ifscCode.message}</span>
                  )}
                  {serverErrors.ifscCode && !errors.ifscCode && (
                    <span className="error-message server-error">{serverErrors.ifscCode}</span>
                  )}
                </div>

                <div className="bank-form-group">
                  <label>MICR Code <span className="required">*</span></label>
                  <div className="input-container">
                    <i className="fas fa-barcode"></i>
                    <input
                      type="text"
                      className={errors.micrCode || serverErrors.micrCode ? 'error' : ''}
                      {...register('micrCode')}
                      placeholder="Enter MICR code"
                    />
                  </div>
                  {errors.micrCode && (
                    <span className="error-message">{errors.micrCode.message}</span>
                  )}
                  {serverErrors.micrCode && !errors.micrCode && (
                    <span className="error-message server-error">{serverErrors.micrCode}</span>
                  )}
                </div>
              </div>

              <div className="bank-form-group">
                <label>Bank Branch Address <span className="required">*</span></label>
                <div className="input-container">
                  <i className="fas fa-map-marker-alt"></i>
                  <textarea
                    rows="2"
                    className={errors.bankAddress || serverErrors.bankAddress ? 'error' : ''}
                    {...register('bankAddress')}
                    placeholder="Full branch address"
                  ></textarea>
                </div>
                {errors.bankAddress && (
                  <span className="error-message">{errors.bankAddress.message}</span>
                )}
                {serverErrors.bankAddress && !errors.bankAddress && (
                  <span className="error-message server-error">{serverErrors.bankAddress}</span>
                )}
              </div>

              <div className="bank-form-group">
                <label>Upload PassBook / Cheque Book Image <span className="required">*</span></label>
                <div className={`file-upload ${errors.bankDocument || serverErrors.bankDocument ? 'error' : ''}`}>
                  <input
                    type="file"
                    id="bankDocument"
                    {...register('bankDocument')}
                    onChange={handleFileChange}
                    accept=".jpg,.jpeg,.png,.pdf"
                  />
                  <label htmlFor="bankDocument">
                    <i className="fas fa-cloud-upload-alt"></i>
                    <p>Click or Drag & Drop to upload file</p>
                    <span>Supports JPG, PNG, PDF - Max 5MB</span>
                  </label>
                </div>
                {filePreview && filePreview !== 'pdf' && (
                  <div className="file-preview">
                    <img src={filePreview} alt="Document preview" />
                    <button type="button" className="preview-download-btn" onClick={downloadDocument}>
                      <i className="fas fa-download"></i> Download
                    </button>
                  </div>
                )}
                {filePreview === 'pdf' && (
                  <div className="file-preview pdf-preview">
                    <i className="fas fa-file-pdf"></i>
                    <p>PDF Document</p>
                    <button type="button" className="preview-download-btn" onClick={downloadDocument}>
                      <i className="fas fa-download"></i> Download
                    </button>
                  </div>
                )}
                {errors.bankDocument && (
                  <span className="error-message">{errors.bankDocument.message}</span>
                )}
                {serverErrors.bankDocument && !errors.bankDocument && (
                  <span className="error-message server-error">{serverErrors.bankDocument}</span>
                )}
              </div>

              <div className="bank-form-actions">
                <button
                  type="button"
                  className="bank-btn bank-btn-cancel"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                >
                  <i className="fas fa-times"></i> Cancel
                </button>
                <button
                  type="submit"
                  className="bank-btn bank-btn-submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i> Processing...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-check"></i> {bankDetails ? 'Update Details' : 'Submit Details'}
                    </>
                  )}
                </button>
              </div>
            </form>
          ) : (
            // DETAILS VIEW (when data exists and not editing)
            <div className="bank-details-content">
              <div className="bank-details-grid">
                <div className="bank-detail-item">
                  <label>Account Holder Name</label>
                  <div className="detail-value">{bankDetails.account_holder_name}</div>
                </div>

                <div className="bank-detail-item">
                  <label>Account Type</label>
                  <div className="detail-value">{bankDetails.account_type}</div>
                </div>

                <div className="bank-detail-item">
                  <label>Account Number</label>
                  <div className="detail-value">{bankDetails.account_no}</div>
                </div>

                <div className="bank-detail-item">
                  <label>Bank Name</label>
                  <div className="detail-value">{bankDetails.bank_name}</div>
                </div>

                <div className="bank-detail-item">
                  <label>Bank Branch</label>
                  <div className="detail-value">{bankDetails.bank_branch}</div>
                </div>

                <div className="bank-detail-item">
                  <label>IFSC Code</label>
                  <div className="detail-value">{bankDetails.ifsc_code}</div>
                </div>

                <div className="bank-detail-item">
                  <label>MICR Code</label>
                  <div className="detail-value">{bankDetails.micr_code}</div>
                </div>

                <div className="bank-detail-item full-width">
                  <label>Bank Branch Address</label>
                  <div className="detail-value">{bankDetails.branch_address}</div>
                </div>

                <div className="bank-detail-item full-width">
                  <label>Bank Document</label>
                  <div className="document-preview">
                    {filePreview && filePreview !== 'pdf' ? (
                      <>
                        <img src={filePreview} alt="Bank document" />
                        <button className="preview-download-btn" onClick={downloadDocument}>
                          <i className="fas fa-download"></i> Download
                        </button>
                      </>
                    ) : filePreview === 'pdf' ? (
                      <>
                        <i className="fas fa-file-pdf pdf-icon"></i>
                        <p>PDF Document</p>
                        <button className="preview-download-btn" onClick={downloadDocument}>
                          <i className="fas fa-download"></i> Download
                        </button>
                      </>
                    ) : (
                      <p>No document uploaded</p>
                    )}
                  </div>
                </div>
              </div>

              {/* <div className="bank-details-actions">
                <button className="bank-btn bank-btn-delete" onClick={handleDelete}>
                  <i className="fas fa-trash"></i> Delete Details
                </button>
              </div> */}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default BankDetails;